import {
  Editor,
  Text,
  Node,
  Range,
  Transforms,
} from 'slate';
import { Message } from '@oneflowab/pomes';
import get from 'lodash/get';

import TextColorIcon from 'components/icons/text-color';
import TextBackgroundColorIcon from 'components/icons/text-background-color';
import TableCellBackgroundColorIcon from 'components/icons/table-cell-background-color';

import {
  APPLY_TO,
  LABEL_CONFIG_BY_APPLY_TO,
  NODE_MATCHER_BY_TYPE,
} from './apply-color-button-constants';
import type { ApplyTo } from './apply-color-button.d';

/**
 * ## Function List
 * - [isTableCell](#isTableCell)
 * - [hasActiveColor](#hasActiveColor)
 * - [hasMultipleColors](#hasMultipleColors)
 * - [getPathColor](#getPathColor)
 * - [getActiveColorNode](#getActiveColorNode)
 * - [getActiveColor](#getActiveColor)
 * - [getLabel](#getLabel)
 * - [isNodeFullySelected](#isNodeFullySelected)
 * - [isAllTextSelectedInNode](#isAllTextSelectedInNode)
 * - [getIconDefinitionByApplyTo](#getIconDefinitionByApplyTo)
 * - [setListItemColor](#setListItemColor)
 *
 * Clicking on a function name will scroll to its definition.
 * NOTE: Any new function added in this file should be added to the list above.
 */

export const isTableCell = (node: Node & { type?: string }) => node?.type === 'table-cell';

export const hasActiveColor = (node: Node, styleToApply: string) => {
  if (node && 'marks' in node && node.marks?.[styleToApply as keyof typeof node.marks]) {
    return true;
  }

  return (Text.isText(node) || isTableCell(node)) && node[styleToApply as keyof typeof node];
};

export const hasMultipleColors = (editor: Editor, styleToApply: string) => {
  const matchedNodes = Editor.nodes(editor, {
    match: (node) => !Editor.isEditor(node) && hasActiveColor(node, styleToApply),
  });

  const firstNode = matchedNodes.next();

  // Either there is only one node with color or there is no matched nodes
  if (firstNode.done) {
    return false;
  }

  const secondNode = matchedNodes.next();

  // There is another color node
  if (secondNode.value && !secondNode.done) {
    return true;
  }

  return false;
};

export const getPathColor = (editor: Editor, selectedColor: string, styleToApply: string) => {
  if (hasMultipleColors(editor, styleToApply)) {
    return 'none';
  }

  return selectedColor;
};

export const getActiveColorNode = (editor: Editor, applyTo: ApplyTo, styleToApply: string) => {
  const isActiveColorNode = (node: Node) => !Editor.isEditor(node)
  && hasActiveColor(node, styleToApply);
  let matchActiveNode = isActiveColorNode;
  if (applyTo === APPLY_TO.TABLE_CELL_BACKGROUND) {
    matchActiveNode = (node) => !Editor.isEditor(node)
    && node
    && 'type' in node
    && node.type === 'table-cell';
  }
  const [match] = Editor.nodes(editor, {
    match: matchActiveNode,
    mode: 'lowest',
  });

  if (!match) {
    return null;
  }

  if (applyTo === APPLY_TO.TABLE_CELL_BACKGROUND) {
    return Editor.nodes(editor, {
      match: matchActiveNode,
      mode: 'lowest',
    });
  }
  return match[0];
};

export const getActiveColor = (
  editor: Editor,
  activeNode: Node & { type?: string },
  styleToApply: string,
  applyTo: ApplyTo,
) => {
  if (isTableCell(activeNode) && applyTo === APPLY_TO.TABLE_CELL_BACKGROUND) {
    return get(activeNode, styleToApply);
  }

  if (editor?.marks?.[styleToApply as keyof typeof editor.marks]) {
    return editor.marks[styleToApply as keyof typeof editor.marks];
  }

  if (
    activeNode
    && 'marks' in activeNode
    && activeNode.marks?.[styleToApply as keyof typeof editor.marks]
  ) {
    return activeNode?.marks?.[styleToApply as keyof typeof editor.marks];
  }

  return get(activeNode, styleToApply);
};

export const getLabel = (applyTo: ApplyTo) => Message(LABEL_CONFIG_BY_APPLY_TO[applyTo]);

/**
 * Checks if the node is fully selected within the editor's selection.
 *
 * @param editor - The editor instance.
 * @param path - The path of the node to check.
 * @returns True if the node is fully selected, false otherwise.
 */
export const isNodeFullySelected = (editor: Editor, path: any) => {
  const nodeRange = Editor.range(editor, path);

  // Fully selected if the node range is completely inside the selection
  return editor.selection && Range.includes(editor.selection, nodeRange);
};

/**
 * Checks if all text in the node is selected.
 *
 * @param editor - The editor instance.
 * @param path - The path of the node to check.
 * @returns True if all text in the node is selected, false otherwise.
 */
export const isAllTextSelectedInNode = (editor: Editor, path: any) => {
  const nodeRange = Editor.range(editor, path);
  const intersection = editor.selection ? Range.intersection(editor.selection, nodeRange) : null;

  if (!intersection) return false;

  const selectedText = Editor.string(editor, intersection);
  const fullText = Editor.string(editor, path);

  return selectedText.length === fullText.length;
};

/**
 * Retrieves the icon definition based on the specified application target and styles.
 *
 * @param applyTo - The target to which the style is applied
 * @param editor - The editor instance used to determine the current state.
 * @param textColor - The color to apply to the text.
 * @param backgroundColor - The color to apply to the background.
 * @param styleToApply - The specific style to apply.
 * @returns The icon definition object containing the component and its properties, or null
 */
export const getIconDefinitionByApplyTo = (
  applyTo: ApplyTo,
  editor: Editor,
  textColor: string,
  backgroundColor: string,
  styleToApply: string,
) => {
  const iconMap = {
    [APPLY_TO.TEXT]: {
      component: TextColorIcon,
      props: {
        pathColor: getPathColor(editor, textColor, styleToApply),
      },
    },
    [APPLY_TO.TEXT_BACKGROUND]: {
      component: TextBackgroundColorIcon,
      props: {
        pathColor: getPathColor(editor, backgroundColor, styleToApply),
      },
    },
    [APPLY_TO.TABLE_CELL_BACKGROUND]: {
      component: TableCellBackgroundColorIcon,
      props: {
        pathColor: getPathColor(editor, backgroundColor, styleToApply),
      },
    },
  };
  return iconMap[applyTo] || null;
};

/**
 * Sets the color of list items in the editor.
 *
 * @param editor - The editor instance.
 * @param color - The color to apply to the list items.
 * @param styleToApply - The style to apply (e.g., 'color' or 'backgroundColor').
 */
export const setListItemColor = (editor: Editor, color: string, styleToApply: string) => {
  const listItems = Array.from(
    Editor.nodes(editor, {
      at: editor.selection,
      match: (n) => NODE_MATCHER_BY_TYPE.includes(n.type),
    }),
  );
  // eslint-disable-next-line no-restricted-syntax
  for (const [, path] of listItems) {
    if (
      isNodeFullySelected(editor, path) && isAllTextSelectedInNode(editor, path)
    ) {
      Transforms.setNodes(
        editor,
        { color }, // this attaches `color` prop to the list-item block
        { at: path },
      );
    }
  }

  Editor.addMark(editor, styleToApply, color);
};
